import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';

interface ParsedRow {
  name: string;
  birth_date: string;
  birth_time: string | null;
  birth_city: string | null;
  email: string | null;
  phone: string | null;
  gender: string | null;
  notes: string | null;
  _valid: boolean;
  _error: string | null;
}

const REQUIRED_COLUMNS = ['name', 'birth_date'];
const EXPECTED_HEADERS = 'name,birth_date,birth_time,birth_city,email,phone,gender,notes';

function parseCSV(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ''));

  return lines.slice(1).map(line => {
    const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const get = (col: string) => {
      const i = headers.indexOf(col);
      return i >= 0 ? (cols[i] || null) : null;
    };

    const name       = get('name') ?? '';
    const birth_date = get('birth_date') ?? '';
    const birth_time = get('birth_time');
    const birth_city = get('birth_city');
    const email      = get('email');
    const phone      = get('phone');
    const gender     = get('gender');
    const notes      = get('notes');

    let _error: string | null = null;
    if (!name) _error = 'Nome é obrigatório';
    else if (!birth_date) _error = 'Data de nascimento é obrigatória';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(birth_date) && !/^\d{2}\/\d{2}\/\d{4}$/.test(birth_date)) _error = 'Data inválida (use AAAA-MM-DD ou DD/MM/AAAA)';

    const normalizedDate = birth_date.includes('/')
      ? birth_date.split('/').reverse().join('-')
      : birth_date;

    return { name, birth_date: normalizedDate, birth_time, birth_city, email, phone, gender, notes, _valid: !_error, _error };
  });
}

export function ProfessionalCSVImport() {
  const { astrologerProfile } = useAstrologerProfile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows]           = useState<ParsedRow[]>([]);
  const [fileName, setFileName]   = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult]       = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);
  const [step, setStep]           = useState<'idle' | 'preview' | 'done'>('idle');

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCSV(text);
      setRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file, 'utf-8');
  }

  async function handleImport() {
    if (!astrologerProfile) return;
    const valid = rows.filter(r => r._valid);
    if (valid.length === 0) return;

    setImporting(true);
    const errors: string[] = [];
    let imported = 0;
    let skipped  = 0;

    for (const row of valid) {
      const payload = {
        astrologer_id:     astrologerProfile.id,
        name:              row.name,
        birth_date:        row.birth_date,
        birth_time:        row.birth_time || null,
        birth_time_unknown: !row.birth_time,
        birth_city:        row.birth_city || null,
        email:             row.email || null,
        phone:             row.phone || null,
        gender:            row.gender?.toUpperCase().slice(0, 1) || null,
        notes:             row.notes || null,
        birth_timezone:    'America/Sao_Paulo',
      };
      const { error } = await supabase.from('astrologer_clients').insert(payload);
      if (error) {
        if (error.code === '23505') skipped++;
        else errors.push(`${row.name}: ${error.message}`);
      } else {
        imported++;
      }
    }

    setResult({ imported, skipped, errors });
    setImporting(false);
    setStep('done');
  }

  function reset() {
    setRows([]);
    setFileName('');
    setResult(null);
    setStep('idle');
    if (fileRef.current) fileRef.current.value = '';
  }

  const validRows   = rows.filter(r => r._valid);
  const invalidRows = rows.filter(r => !r._valid);
  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1.25rem' };

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', margin: '0 0 0.5rem', fontFamily: 'var(--font-display)' }}>
        Importar Clientes via CSV
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', margin: '0 0 1.5rem' }}>
        Importe múltiplos clientes de uma só vez usando um arquivo CSV.
      </p>

      {step === 'idle' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Template instructions */}
          <div style={cardStyle}>
            <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Formato esperado</p>
            <p style={{ margin: '0 0 0.75rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              O arquivo CSV deve ter a primeira linha como cabeçalho. Colunas aceitas:
            </p>
            <code style={{ display: 'block', background: 'var(--bg-base)', padding: '0.75rem', borderRadius: '6px', fontSize: '12px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
              {EXPECTED_HEADERS}
            </code>
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {REQUIRED_COLUMNS.map(c => (
                <p key={c} style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  <span style={{ color: '#f87171' }}>*</span> <strong>{c}</strong>, obrigatório
                </p>
              ))}
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>birth_date, formato AAAA-MM-DD ou DD/MM/AAAA</p>
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>gender, M ou F (opcional)</p>
            </div>
          </div>

          {/* Example */}
          <div style={cardStyle}>
            <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Exemplo</p>
            <code style={{ display: 'block', background: 'var(--bg-base)', padding: '0.75rem', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.8, whiteSpace: 'pre' }}>
{`name,birth_date,birth_time,birth_city,email,gender
Maria Silva,1990-05-15,14:30,São Paulo,maria@email.com,F
João Costa,1985-11-03,,Rio de Janeiro,,M`}
            </code>
          </div>

          {/* Upload */}
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: 'none' }} id="csv-upload" />
            <label htmlFor="csv-upload" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', background: 'var(--bg-base)', border: '2px dashed var(--border-subtle)', borderRadius: '10px', padding: '2rem 3rem', color: 'var(--text-muted)', fontSize: 'var(--text-label)', transition: 'border-color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--petrol)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
            >
              📁 Selecionar arquivo CSV
            </label>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{fileName}</p>
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                {rows.length} linha{rows.length !== 1 ? 's' : ''} · {validRows.length} válida{validRows.length !== 1 ? 's' : ''} · {invalidRows.length} com erro
              </p>
            </div>
            <button className="btn-secondary" style={{ fontSize: '12px' }} onClick={reset}>Trocar arquivo</button>
          </div>

          {invalidRows.length > 0 && (
            <div style={{ ...cardStyle, borderColor: '#f87171', background: 'rgba(248,113,113,0.05)' }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: '#f87171', fontSize: 'var(--text-label)' }}>Linhas com erro (serão ignoradas)</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {invalidRows.map((r, i) => (
                  <p key={i} style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                    <strong>{r.name || `Linha ${i + 1}`}</strong>: {r._error}
                  </p>
                ))}
              </div>
            </div>
          )}

          {validRows.length > 0 && (
            <div style={cardStyle}>
              <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                Prévia, {validRows.length} cliente{validRows.length !== 1 ? 's' : ''} a importar
              </p>
              <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {validRows.map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem', background: 'var(--bg-base)', borderRadius: '6px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 0.1rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-caption)' }}>{r.name}</p>
                      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                        {new Date(r.birth_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                        {r.birth_time && ` ${r.birth_time}`}
                        {r.birth_city && ` · ${r.birth_city}`}
                        {r.gender && ` · ${r.gender}`}
                      </p>
                    </div>
                    <span style={{ fontSize: '11px', color: '#4ade80', flexShrink: 0 }}>✓</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {validRows.length > 0 && (
            <button className="btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? `Importando ${validRows.length} clientes…` : `Importar ${validRows.length} cliente${validRows.length !== 1 ? 's' : ''} ✦`}
            </button>
          )}
        </div>
      )}

      {step === 'done' && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ ...cardStyle, textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--petrol)', margin: '0 0 0.5rem' }}>✦</p>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '1.1rem', margin: '0 0 0.5rem' }}>Importação concluída</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', margin: 0 }}>
              {result.imported} importado{result.imported !== 1 ? 's' : ''}
              {result.skipped > 0 && ` · ${result.skipped} duplicado${result.skipped !== 1 ? 's' : ''} ignorado${result.skipped !== 1 ? 's' : ''}`}
            </p>
          </div>
          {result.errors.length > 0 && (
            <div style={{ ...cardStyle, borderColor: '#f87171', background: 'rgba(248,113,113,0.05)' }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: '#f87171', fontSize: 'var(--text-label)' }}>Erros durante a importação</p>
              {result.errors.map((e, i) => (
                <p key={i} style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{e}</p>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn-secondary" onClick={reset}>Importar outro arquivo</button>
            <a href="/profissional/clientes" className="btn-primary" style={{ flex: 1, textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Ver clientes →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
